import React, { Component } from 'react';
import {
  Save,
  Warning,
  Share,
  ContentCopy,
} from '@mui/icons-material';
import { Box, Button, Chip } from '@mui/material';
import { connect, ConnectedProps } from 'react-redux';
import Driver from 'driver.js';
import {
  injectIntl,
  FormattedMessage,
} from 'react-intl';
import moment from 'moment';
import 'driver.js/dist/driver.min.css';

import ReactToPrint from 'react-to-print';
import Survey from '../components/survey/Survey';
import GenericDialog from '../components/GenericDialog';
import FeedbackDialog from '../components/survey/FeedbackDialog';
import ContentBox from '../components/layout/ContentBox';
import Flow, { FlowType } from '../components/Flow';
import FinaliseDialog from '../components/survey/FinaliseDialog';
import NotificationBox from '../components/layout/NotificationBox';
import EmptySubmissionSectionWarning from '../components/EmptySubmissionSectionWarning';
import SurveyTemplate from '../components/print/survey/SurveyTemplate';
import { getHighlightStepArray } from '../modules/survey/highlight';
import {
  createOrUpdateSubmission,
  createSharedLink,
  clearSurveyRecord,
  getSharedLink,
  deleteSharedLink,
  fetchTrialAndSurvey,
  fetchTrialWithUserEnrollmentAndSumbission,
  updateTrialsSurvey,
  restoreSurveyPayload,
  setSurveyPayloadRestored,
  showNotification,
  showErrorNotification,
  fetchLatestSurveyHistoryDiff,
  extendSurveyWithHighlight,
  setHighlightStep,
} from '../redux/actions';
import { getBaseUrl } from '../utilities/urlHelper';
import {
  RootState,
  Survey as SurveyType,
  Trial as TrialType,
  Enrollment,
  Submission,
} from '../redux/types';
import withRouter, { RouteType } from '../hoc/withRouter';

type State = {
  showFeedbackDialogForId?: boolean;
  notComplete: boolean;
  saveFailed: boolean;
  saved: boolean;
}

type EnrollmentWithSubmission = Enrollment & {
  submission?: Submission;
};
class MySurveyView extends Component<Props, State> {
  private flow = React.createRef<Flow>();

  private componentRef = React.createRef<SurveyTemplate>();

  constructor(props: Props) {
    super(props);
    this.state = {
      showFeedbackDialogForId: undefined,
      saved: false,
      saveFailed: false,
      notComplete: false,
    };
  }

  componentDidMount(): void {
    // if trial object already in redux store
    const {
      trial,
      user,
      router,
      fetchTrialWithUserEnrollmentAndSumbission:
      fetchTrialWithUserEnrollmentAndSubmissionAction,
      fetchTrialAndSurvey: fetchTrialAndSurveyAction,
    } = this.props;
    if (trial?.id) {
      return;
    }
    if (user?.role !== 'admin') {
      // set trial with survey, Enrollment and Submission
      const enrollmentId = router.params?.enrollmentId;
      fetchTrialWithUserEnrollmentAndSubmissionAction(Number(enrollmentId));
    } else {
      // otherwise fetch the data based on the trial id passed in url param and store in redux store
      const trialId = router.params?.trialId;
      // set trial with enrollment and survey
      fetchTrialAndSurveyAction(Number(trialId));
    }
  }

  componentDidUpdate(): void {
    const {
      user,
      trial,
      sharedLink,
      surveyPayloadRestored,
      surveyHistoryDiff,
      isFetchedHistory,
      intl,
      getSharedLink: getSharedLinkAction,
      setSurveyPayloadRestored: setSurveyPayloadRestoredAction,
      fetchLatestSurveyHistoryDiff: fetchLatestSurveyHistoryDiffAction,
      extendSurveyWithHighlight: extendSurveyWithHighlightAction,
      setHighlightStep: setHighlightStepAction,
    } = this.props;
    if (!user) {
      return;
    }
    if (trial?.id
      && user.role
      && user.role === 'admin') {
      if (!sharedLink) {
        getSharedLinkAction('survey', 'code', trial.code);
      }
    }

    if (surveyPayloadRestored === true) {
      // do not allow to submit if finalised, but not complete
      setSurveyPayloadRestoredAction(false);
      if (!this.checkForCompletion()) {
        this.setNotComplete();
        return;
      }
      if (this.flow.current) {
        this.flow.current.start();
      }
    }

    if (trial?.survey
      && !surveyHistoryDiff
      && !isFetchedHistory
    ) {
      const surveyId = trial.survey.id;
      const dateOnwards = trial.registrationEndDate;
      fetchLatestSurveyHistoryDiffAction(surveyId, dateOnwards);
    }

    if (
      trial?.survey && surveyHistoryDiff
      && Object.entries(surveyHistoryDiff).length && !trial.survey.extended
    ) {
      extendSurveyWithHighlightAction(trial.survey, surveyHistoryDiff);
      const stepArray = getHighlightStepArray(
        surveyHistoryDiff,
        0,
        intl,
        trial.survey.updatedAt,
      );
      setHighlightStepAction(stepArray);
    }
  }

  componentWillUnmount(): void {
    const {
      clearSurveyRecord: clearSurveyRecordAction,
    } = this.props;
    clearSurveyRecordAction();
  }

  private onSectionChange = (sectionIndex: number): void => {
    const {
      intl,
      trial,
      surveyHistoryDiff,
      highlightStepArray,
      setHighlightStep: setHighlightStepAction,
    } = this.props;
    const historyDiff = surveyHistoryDiff;
    if (!historyDiff && highlightStepArray !== undefined) {
      setHighlightStepAction(undefined);
    } else if (historyDiff) {
      const stepArray = getHighlightStepArray(
        historyDiff,
        sectionIndex,
        intl,
        trial?.survey?.updatedAt,
      );
      setHighlightStepAction(stepArray);
    }
  };

  setNotComplete = (): void => {
    this.setState({ notComplete: true });
  };

  private startHighlight = (): void => {
    const { highlightStepArray } = this.props;
    const stepArray = highlightStepArray;
    if (!stepArray) {
      return;
    }
    const driver = new Driver();
    driver.defineSteps(stepArray);
    setTimeout(() => { driver.start(); }, 0);
  };

  sendSubmission = (
    survey: SurveyType,
    enrollment: EnrollmentWithSubmission,
    finalised: boolean,
  ): any => {
    const {
      submissionPayload,
      createOrUpdateSubmission: createOrUpdateSubmissionAction,
    } = this.props;
    const submission = {
      lastUpdatedAt: moment.utc(),
      surveyId: survey.id,
      enrollmentId: enrollment.id,
      quipUserId: enrollment.attendeeId,
      feedbackProvided: enrollment.submission && enrollment.submission.feedbackProvided,
      payload: submissionPayload,
      finalised,
    };

    return createOrUpdateSubmissionAction(submission);
  };

  checkForCompletion = (): boolean => {
    const {
      trial,
      submissionPayload,
      updateTrialsSurvey: updateTrialsSurveyAction,
    } = this.props;
    if (!trial) {
      return false;
    }
    const survey = trial.survey as SurveyType;
    const submission = submissionPayload;
    const sections = survey?.payload?.sections ? survey?.payload?.sections : [];
    type SubmissionAnswer = Submission['payload']['sections'][0]['questions'][0]['answers'][0];
    const filterNoAnswer = (answer: SubmissionAnswer): boolean => {
      if (!answer || (answer.type !== 'MultipleChoice' && !answer.answer)) {
        return true;
      }
      if (answer.type === 'MultipleChoice') {
        if (
          !answer.choices
          || (answer.choices && Object.values(answer.choices).indexOf(true) === -1)
        ) {
          return true;
        }
      }
      return false;
    };
    for (let sIdx = 0; sIdx < sections.length; sIdx += 1) {
      const section = sections[sIdx];
      const casesLength = section.numCases;
      const questions = section.questions.filter((question) => question.required);
      const submittedSection = submission.sections[sIdx];
      if (submittedSection.discardSubmission) {
        // eslint-disable-next-line no-continue
        continue;
      }
      if (!submittedSection) {
        return false;
      }

      // filter out context questions
      const requiredContextQuestions = section.contextQuestions
        .filter((question) => question.required)
        .map((question) => (question.question.uuid));

      const checkIfComplete = (uuid: string) => {
        const answer = submittedSection.context[uuid];
        if (Array.isArray(answer)) {
          return answer.length > 0;
        }

        // to check free text questions
        if (answer && !answer.answer) {
          return false;
        }

        return submittedSection.context[uuid];
      };

      const isContextQuestionComplete = requiredContextQuestions.every(checkIfComplete);

      if (!isContextQuestionComplete) {
        return false;
      }

      // set appropiate marker for incomplete main questions
      for (let qIdx = 0; qIdx < questions.length; qIdx += 1) {
        const questionUuid = questions[qIdx].question.uuid;
        const submittedQuestion = submittedSection.questions[questionUuid];
        if (!submittedQuestion
          || submittedQuestion.answers.filter(Boolean).length !== casesLength // clear sparse array
          || submittedQuestion.answers.filter(filterNoAnswer).length > 0) {
          const newSurvey: SurveyType = {
            ...survey,
            payload: {
              ...survey.payload,
              sections: survey.payload.sections.map((sec, sIndex) => {
                if (sIndex !== sIdx) {
                  return sec;
                }
                return {
                  ...sec,
                  questions: sec.questions.map((question) => {
                    if (questionUuid !== question.question.uuid) {
                      return question;
                    }
                    return {
                      ...question,
                      incomplete: true,
                    };
                  }),
                };
              }),
            },
          };
          updateTrialsSurveyAction(newSurvey);
          return false;
        }
      }
    }

    return true;
  };

  handleSubmit = async (
    survey: SurveyType,
    enrollment: EnrollmentWithSubmission,
    finalised: boolean,
  ): Promise<void> => {
    const {
      showNotification: showNotificationAction,
      showErrorNotification: showErrorNotificationAction,
    } = this.props;
    this.setState({ notComplete: false });
    try {
      const updatedSubmission = await this.sendSubmission(survey, enrollment, finalised);
      if (finalised) {
        this.setState({ showFeedbackDialogForId: updatedSubmission.id });
      } else {
        showNotificationAction(<FormattedMessage id="survey.saved" />);
        this.setState({ saveFailed: false });
      }
    } catch (error) {
      showErrorNotificationAction(<FormattedMessage id="survey.finalizedFailed" />);
      this.setState({ saveFailed: true });
    }
  };

  submissionApproved = (): boolean => {
    const { submissionPayload } = this.props;
    return !!submissionPayload;
  };

  handleSave = (survey: SurveyType, enrollment: EnrollmentWithSubmission): void => {
    this.handleSubmit(survey, enrollment, false);
  };

  submissionHasEmptySections = (): boolean => {
    const { submissionPayload, trial } = this.props;
    const survey = trial?.survey;
    return (survey as SurveyType).payload.sections
      .some((_, sectionIdx) => !submissionPayload.sections[sectionIdx]);
  };

  flowSteps = (
    trial: TrialType,
    survey: SurveyType,
    enrollment: EnrollmentWithSubmission,
  ): FlowType[] => {
    const {
      fetchTrialWithUserEnrollmentAndSumbission: fetchTrialWithUserEnrollmentAndSumbissionAction,
    } = this.props;
    const { saveFailed, showFeedbackDialogForId } = this.state;
    return [
      {
        element: (
          <FinaliseDialog
            isOpen
            onCancel={(): void | null => this.flow.current && this.flow.current.stop()}
            onFinalisation={
              (): Promise<void> | null => this.flow.current && this.flow.current.next()
            }
          />
        ),
      },
      {
        element: (
          <GenericDialog isOpen>
            <EmptySubmissionSectionWarning
              onContinue={(): Promise<void> | null => this.flow.current && this.flow.current.next()}
              onCancel={(): void | null => this.flow.current && this.flow.current.stop()}
            />
          </GenericDialog>
        ),
        skipCondition: (): boolean => !this.submissionHasEmptySections(),
        onNext: (): Promise<void> => this.handleSubmit(survey, enrollment, true),
      },
      {
        element: (
          <GenericDialog isOpen>
            <FeedbackDialog
              surveyId={survey.id}
              submissionId={showFeedbackDialogForId}
              trialName={trial.name}
              onClose={(): Promise<void> | null => this.flow.current && this.flow.current.next()}
            />
          </GenericDialog>
        ),
        skipCondition: (): boolean => saveFailed,
        onNext: (): void => {
          if (this.flow.current) {
            this.flow.current.stop();
            fetchTrialWithUserEnrollmentAndSumbissionAction(enrollment.id);
          }
        },
      },
    ];
  };

  renderButtons = (): React.ReactNode => {
    const { saved, saveFailed, notComplete } = this.state;
    const { trial, restoreSurveyPayload: restoreSurveyPayloadAction } = this.props;
    const enrollment = trial?.enrollment;
    const submission = enrollment?.submission;

    if (!enrollment) {
      return null;
    }

    return (
      <Box display="flex" sx={{ my: 2 }} gap={1}>
        <Button
          variant="contained"
          id="SurveySubmitButton"
          onClick={(): void => {
            this.handleSave((trial?.survey as SurveyType), enrollment);
          }}
          disabled={
            !this.submissionApproved()
            || (submission && submission.finalised)
          }
          color="primary"
        >
          <FormattedMessage id="save" />
        </Button>
        <Button
          variant="contained"
          id="FinalizeSubmitButton"
          onClick={(): void => {
            restoreSurveyPayloadAction();
          }}
          color="secondary"
          disabled={
            !this.submissionApproved()
            || (submission && submission.finalised)
          }
        >
          <FormattedMessage id="survey.confirmFinalisationButtonLabel" />
        </Button>
        {
          saved && (
            <span className="color-quip ml3 inline-flex mt3">
              <Save color="primary" />
              <FormattedMessage id="survey.saved" />
            </span>
          )
        }
        {
          saveFailed && (
            <span className="color-error ml3 inline-flex mt3">
              <Warning color="error" />
              <FormattedMessage id="survey.saveFailed" />
            </span>
          )
        }
        {
          notComplete && (
            <span className="color-error ml3 inline-flex mt3">
              <Warning color="error" />
              <FormattedMessage id="survey.notComplete" />
            </span>
          )
        }
      </Box>
    );
  };

  renderShareButton = (): React.ReactNode => {
    // check if its an admin login
    const { trial } = this.props;
    const {
      user,
      createSharedLink: _createSharedLink,
      deleteSharedLink: _deleteSharedLink,
      sharedLink,
      showNotification: showNotificationAction,
    } = this.props;
    const role = user?.role;

    if (role !== 'admin' || !trial) {
      return '';
    }

    const buttonMessage = sharedLink ? <FormattedMessage id="survey.deleteShareLink" /> : <Share />;
    const buttonColor = sharedLink ? 'secondary' : 'primary';
    const toCreateSharedLink = (): void => {
      const itemObject = { code: trial.code };
      _createSharedLink('survey', itemObject);
    };

    const toDeleteSharedLink = (): void => {
      if (sharedLink) {
        _deleteSharedLink(sharedLink);
      }
    };
    const functionCall = sharedLink ? toDeleteSharedLink : toCreateSharedLink;
    const sharedLinkText = sharedLink ? `${getBaseUrl()}/pblc?token=${sharedLink}` : '';
    return (
      <Box sx={{ mb: 1 }}>
        <Button
          variant="contained"
          id="FinalizeSubmitButton"
          color={buttonColor}
          onClick={(): void => functionCall()}
        >
          {buttonMessage}
        </Button>
        {sharedLink && (
          <Box sx={{ display: 'inline', ml: 1 }}>
            <Chip
              label={sharedLinkText}
              variant="outlined"
              color="info"
              icon={<ContentCopy />}
              onClick={(): void => {
                navigator.clipboard.writeText(sharedLinkText).then(() => {
                  showNotificationAction('Link copied to clipboard');
                });
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  renderPrintButton = (): React.ReactNode => (
    <ReactToPrint
      trigger={() => (
        <Button
          variant="contained"
          color="primary"
        >
          <FormattedMessage id="enrollmentsmanager.detailedEvaluationPrintButton" />
        </Button>
      )}
      content={(): SurveyTemplate | null => this.componentRef.current}
    />
  );

  render(): React.ReactNode {
    const {
      trial,
      surveyHistoryDiff,
      highlightStepArray,
      submissionPayload,
    } = this.props;
    const survey = trial?.survey;
    const enrollment = trial?.enrollment;
    const submission = enrollment && enrollment.submission;

    if (!trial || !survey) {
      return (
        <NotificationBox>
          <FormattedMessage id="trialmanagement.surveyUnavailable" />
        </NotificationBox>
      );
    }
    return (
      trial
      && survey && (
        <ContentBox
          key={trial.id}
          title={trial.name}
        >
          {
            surveyHistoryDiff && highlightStepArray
            && highlightStepArray.length > 0
            && (
              <div>
                <NotificationBox>
                  <FormattedMessage id="survey.surveyHighlight" />
                  <Button onClick={this.startHighlight} style={{ marginLeft: '10px' }} variant="contained" size="small"><FormattedMessage id="review.reviewSubmissionButton" /></Button>
                </NotificationBox>
              </div>
            )
          }
          {
            submission && submission.finalised && (
              <div style={{ marginBottom: 20 }}>
                <NotificationBox>
                  <FormattedMessage id="survey.finalisedNote" />
                </NotificationBox>
              </div>
            )
          }
          <Survey
            surveyPayload={survey.payload}
            submissionPayload={submissionPayload}
            onCurrentSectionChange={this.onSectionChange}
            optionalSections={!!survey.optionalSections}
          />
          {this.renderButtons()}
          {this.renderShareButton()}
          {this.renderPrintButton()}
          <div style={{ display: 'none' }}>
            <SurveyTemplate
              data={{ trial, submission: submissionPayload }}
              ref={this.componentRef}
            />
          </div>
          {
            enrollment && (
              <Flow
                ref={this.flow}
                steps={this.flowSteps(trial, survey, enrollment)}
              />
            )
          }
        </ContentBox>
      )
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  // previously survey submission payload was kept in store.survey.
  // this is added not to break old changes. perhaps refactor old components
  submissionPayload: state.submission.payload,
  user: state.users.token,
  sharedLink: state.publicSharedLink.sharedLink,
  trial: state.trial.trial,
  surveyPayloadRestored: state.trial.surveyPayloadRestored,
  surveyHistoryDiff: state.surveyV2.surveyHistoryDiff,
  isFetchedHistory: state.surveyV2.isFetchedHistory,
  highlightStepArray: state.surveyV2.highlightStepArray,
  intl: state.intl,
});

const dispatchProps = {
  createOrUpdateSubmission,
  createSharedLink,
  clearSurveyRecord,
  getSharedLink,
  deleteSharedLink,
  fetchTrialAndSurvey,
  fetchTrialWithUserEnrollmentAndSumbission,
  updateTrialsSurvey,
  restoreSurveyPayload,
  setSurveyPayloadRestored,
  showNotification,
  showErrorNotification,
  fetchLatestSurveyHistoryDiff,
  extendSurveyWithHighlight,
  setHighlightStep,
};

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type MatchParams = {
  name: string;
  trialId: string;
  enrollmentId: string;
}
type Props = PropsFromRedux & RouteType<MatchParams>;

export default injectIntl(connector(withRouter(MySurveyView)));
